import { isEqual, uniq } from 'lodash';
import React, { useEffect, useState } from 'react';
import { getInvalidPGFilterKeys } from '@utils/CountryUtils';
import { PagesConfig } from '@utils/pageConfig/PagesConfigContext';
import { FilterKeys, Option, ProductGroupFeatureValue } from '@utils/pageConfig/Types';
import { WithAlert } from '@components/design';

interface WarningProductGroupFilterContainerProps {
  pagesConfig: PagesConfig;
}

const WarningProductGroupFilterContainer: React.FC<WarningProductGroupFilterContainerProps> = ({ pagesConfig }) => {
  const { appliedFilters, productGroupFeatureValueList, pageType, pgsByAppliedCountry } = pagesConfig;
  const [isDismissWarning, setIsDismissWarning] = useState<boolean>(false);
  const [showWarning, setShowWarning] = useState<boolean>(false);

  useEffect(() => {
    setIsDismissWarning(false);
    setShowWarning(false);
    const { selectedFilters } = appliedFilters[pageType];
    let selectedPGs: Option[] = selectedFilters
      .filter((filter) => filter.filterKey === FilterKeys.PRODUCT_GROUP)
      .flatMap((filter) => filter.options);

    const appliedCountries: Option[] = selectedFilters
      .filter((filter) => filter.filterKey === FilterKeys.COUNTRY)
      .flatMap((filter) => filter.options);

    // when all pg is selected, the options array in the pagesConfig.appliedFilters[pageType].selectedFilters won't show, hence the checking
    if (selectedPGs.length === 0) {
      selectedPGs = getAllProductGroups(productGroupFeatureValueList);
    }

    // based on selected filter keys, get Filter keys that are not available from the list of pg
    const unavailableFilterKeys: FilterKeys[] = uniq(
      selectedPGs.flatMap((pg) => getInvalidPGFilterKeys(pg, productGroupFeatureValueList, selectedFilters)),
    );

    // show warning of there are unavailable filter keys found
    if (unavailableFilterKeys.length > 0 && isEqual(pgsByAppliedCountry[0]?.country, appliedCountries[0]?.value)) {
      setShowWarning(true);
    }
  }, [appliedFilters[pageType].selectedFilters, pagesConfig.productGroupFeatureValueList]);

  return !isDismissWarning && showWarning ? (
    <div className="my-4 bg-white">
      <WithAlert
        title="Only product categories that return data based on your selections are displayed"
        type="info"
        onClick={() => {
          setIsDismissWarning(true);
        }}
      />
    </div>
  ) : null;
};

/**
 * Get all product groups from productGroupFeatureValueList, form into an array of Option[]
 * @param productGroupFeatureValueList contains all product groups with side menu filter for selected countries
 */
const getAllProductGroups = (productGroupFeatureValueList: ProductGroupFeatureValue[]) => {
  return productGroupFeatureValueList.map((pg) => ({
    value: pg.productGroup,
    label: pg.productGroup,
  }));
};

export default WarningProductGroupFilterContainer;
